import { graphql, Script, useStaticQuery } from "gatsby";
import React, { Fragment, useEffect, useState } from "react";
import { ConsentProvider } from "../../consent/ConsentContext";
import { ConsentScripts } from "../../consent/ConsentScripts";
import { CookieConsentBanner } from "../../consent/CookieConsentBanner";
import Footer from "./footer";
import PromoBar from "./promobar";
import Popup from "./Popup";
import { Toaster } from "react-hot-toast";
import ExitIntent from "../utils/ExitIntent";
import ExitPopup from "./ExitPopup";
import { MobileHomeOrderBar } from "../home/MobileHomeOrderBar";

const Layout = ({ children, location }) => {
  const data = useStaticQuery(graphql`
    {
      allSanityPromobar {
        nodes {
          id
          title
          linkType
          slug {
            current
          }
          link
        }
      }
      sanityFooter {
        loyaltyButtonTitle
        signupPopup {
          state
          heading
          subheading
          image {
            asset {
              gatsbyImageData(
                layout: CONSTRAINED
                placeholder: BLURRED
                formats: WEBP
              )
            }
          }
        }
        enableExitIntent
        exitPopup {
          state
          heading
          description
          image {
            asset {
              gatsbyImageData(
                layout: CONSTRAINED
                placeholder: BLURRED
                formats: WEBP
              )
            }
          }
          button {
            buttonText
            buttonType
            buttonColor
            linkType
            pdf {
              asset {
                url
              }
            }
            slug {
              current
            }
            url
          }
          button2 {
            buttonText
            buttonType
            buttonColor
            linkType
            pdf {
              asset {
                url
              }
            }
            slug {
              current
            }
            url
          }
        }
      }
    }
  `);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hidePopup, setHidePopup] = useState(false);
  const initialPathname = location?.pathname || "";
  const [isOrderPage, setIsOrderPage] = useState(initialPathname === "/order");
  const [pathname, setPathname] = useState(initialPathname);
  const [exitPopup, setExitPopup] = useState(false);
  const mainClassName = "w-full mx-auto";
  const delayedOrderBarPaths = new Set([
    "/",
    "/accessibility-statement",
    "/careers",
    "/catering-menu",
    "/contact",
    "/giftcard",
    "/privacy-policy",
  ]);
  const showOrderBarImmediately =
    pathname !== "" && !delayedOrderBarPaths.has(pathname);

  useEffect(() => {
    const currentPathname =
      location?.pathname ||
      (typeof window !== "undefined" ? window.location.pathname : "");
    setPathname(currentPathname);
    setIsOrderPage(currentPathname === "/order");
    if (document.cookie.indexOf("exit_modal_seen=true") < 0) {
      const removeExitIntent = ExitIntent({
        threshold: 30,
        eventThrottle: 100,
        onExitIntent: () => {
          setExitPopup(true);
          let expiryDate = new Date(Date.now() + 1 * (1000 * 60 * 60 * 1));
          document.cookie =
            "exit_modal_seen" + "=true; expires=" + expiryDate.toUTCString();
        },
      });
      return () => {
        removeExitIntent();
      };
    }
  }, []);

  return (
    <ConsentProvider>
      <Fragment>
        <Script strategy="idle">{`(function(d){var s = d.createElement("script");s.setAttribute("data-account", "Wfu8weRT62");s.setAttribute("src", "https://cdn.userway.org/widget.js");(d.body || d.head).appendChild(s);})(document)`}</Script>
        <div className="relative max-w-[100vw] w-full h-full mx-auto">
          <PromoBar
            allPromos={data.allSanityPromobar.nodes}
            isOrderPage={isOrderPage}
          />
          <main id="main-content" className={mainClassName}>
            {children}
          </main>
          {pathname && !isOrderPage && (
            <MobileHomeOrderBar showImmediately={showOrderBarImmediately} />
          )}
          {!hidePopup &&
            data.sanityFooter.loyaltyButtonTitle &&
            data.sanityFooter.signupPopup.state && (
              <div className="bg-black text-white font-nunito font-medium px-5 py-2 fixed bottom-5 right-5 left-auto sm:left-auto flex items-center gap-2 z-10 flex-wrap w-fit">
                <button
                  aria-label="Close popup"
                  onClick={() => setHidePopup(true)}
                  type="button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="size-6"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18 18 6M6 6l12 12"
                    />
                  </svg>
                </button>{" "}
                <button
                  onClick={() => setIsModalOpen(true)}
                  className=""
                  type="button"
                  aria-label={
                    data.sanityFooter.loyaltyButtonTitle ||
                    "Open loyalty signup popup"
                  }
                >
                  {data.sanityFooter.loyaltyButtonTitle}
                </button>
              </div>
            )}
          <Popup
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            data={data.sanityFooter.signupPopup}
          />
          <Toaster />{" "}
          {data.sanityFooter.enableExitIntent && data.sanityFooter.exitPopup && (
            <ExitPopup
              data={data.sanityFooter.exitPopup}
              onClose={() => setExitPopup(false)}
              value={exitPopup}
            />
          )}
          <Footer />
        </div>
        <CookieConsentBanner />
        <ConsentScripts />
      </Fragment>
    </ConsentProvider>
  );
};

export default Layout;
